import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import API_URL from '../api';
import './ResumeScore.css';

const CATEGORY_LABELS = {
  open_source: 'Open Source',
  self_projects: 'Self Projects',
  production: 'Production Experience',
  technical_skills: 'Technical Skills',
};

function scoreColor(pct) {
  if (pct >= 0.75) return '#16a34a';
  if (pct >= 0.5) return '#ca8a04';
  return '#dc2626';
}

/**
 * Resume score panel.
 * - Self mode (no studentId): the logged-in student scores their own resume.
 * - Staff mode (studentId set): a recruiter/admin views/triggers a candidate's score.
 */
export default function ResumeScore({ studentId = null }) {
  const token = localStorage.getItem('token');
  const isStaff = !!studentId;

  const getUrl = isStaff
    ? `${API_URL}/api/evaluations/student/${studentId}`
    : `${API_URL}/api/evaluations/me`;
  const postUrl = isStaff
    ? `${API_URL}/api/evaluations/student/${studentId}/score`
    : `${API_URL}/api/evaluations/score`;

  const [evaluation, setEvaluation] = useState(null);
  const [loading, setLoading] = useState(true);
  const [scoring, setScoring] = useState(false);
  const [error, setError] = useState(null);

  const authHeader = { headers: { Authorization: `Bearer ${token}` } };

  const load = useCallback(async () => {
    try {
      const res = await axios.get(getUrl, authHeader);
      setEvaluation(res.data);
    } catch {
      setEvaluation(null);
    } finally {
      setLoading(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getUrl]);

  useEffect(() => { load(); }, [load]);

  const runScore = async () => {
    setScoring(true);
    setError(null);
    try {
      const res = await axios.post(postUrl, {}, authHeader);
      setEvaluation(res.data);
    } catch (err) {
      setError(err.response?.data?.error || 'Scoring failed. Please try again.');
    } finally {
      setScoring(false);
    }
  };

  const completed = evaluation && evaluation.status === 'completed' && evaluation.overall;

  return (
    <div className="rs-card">
      <div className="rs-header">
        <div>
          <h3 className="rs-title">AI Resume Score</h3>
          <p className="rs-sub">
            {isStaff ? "Objective evaluation of this candidate's resume."
                     : 'Get an objective, evidence-based score for your resume.'}
          </p>
        </div>
        <button className="rs-btn" onClick={runScore} disabled={scoring}>
          {scoring ? 'Scoring…' : completed ? 'Rescore' : 'Score resume'}
        </button>
      </div>

      {scoring && (
        <div className="rs-progress">
          Running resume → GitHub → LLM evaluation. This can take up to a minute…
        </div>
      )}

      {error && <div className="rs-error">{error}</div>}

      {loading && <div className="rs-muted">Loading…</div>}

      {!loading && !evaluation && !scoring && (
        <div className="rs-empty">
          No score yet. {isStaff ? '' : 'Make sure your resume is uploaded, then '}
          click <strong>Score resume</strong> to generate one.
        </div>
      )}

      {!loading && evaluation && evaluation.status === 'failed' && (
        <div className="rs-error">
          Last run failed: {evaluation.error || 'unknown error'}
        </div>
      )}

      {completed && <ScoreDetail evaluation={evaluation} isStaff={isStaff} />}
    </div>
  );
}

// `isStaff` drives whether the numbers are rendered at all.
//
// Self mode (a student reviewing their own resume) keeps the gauge and the
// per-category bars — the score IS the feedback they came for.
//
// Staff mode (a recruiter viewing a candidate) shows the same analysis with
// every number removed: the product rule is that recruiters never see AI
// scores, percentages or rankings. The evidence, strengths and improvement
// notes carry all the qualitative signal without the number.
function ScoreDetail({ evaluation, isStaff }) {
  const { overall } = evaluation;
  const pct = overall.out_of ? overall.total / overall.out_of : 0;
  const color = scoreColor(pct);
  const categories = evaluation.categories || overall.categories || {};

  return (
    <div className="rs-detail">
      {!isStaff && (
        <div className="rs-overall">
          <div className="rs-gauge" style={{ borderColor: color }}>
            <span className="rs-gauge-num" style={{ color }}>{overall.total}</span>
            <span className="rs-gauge-max">/ {overall.out_of}</span>
          </div>
          <div className="rs-overall-meta">
            {overall.bonus_points > 0 && <div>Bonus: +{overall.bonus_points}</div>}
            {overall.deductions > 0 && <div>Deductions: −{overall.deductions}</div>}
            <div className="rs-muted rs-model">
              {evaluation.model} · {evaluation.provider}
            </div>
          </div>
        </div>
      )}

      <div className="rs-bars">
        {Object.entries(categories).map(([key, data]) => {
          const cpct = data.max ? Math.min(data.score / data.max, 1) : 0;
          return (
            <div key={key} className="rs-bar-row">
              <div className="rs-bar-label">
                <span>{CATEGORY_LABELS[key] || key}</span>
                {!isStaff && <span className="rs-bar-val">{data.score}/{data.max}</span>}
              </div>
              {!isStaff && (
                <div className="rs-bar-track">
                  <div
                    className="rs-bar-fill"
                    style={{ width: `${cpct * 100}%`, background: scoreColor(cpct) }}
                  />
                </div>
              )}
              {data.evidence && <div className="rs-evidence">{data.evidence}</div>}
            </div>
          );
        })}
      </div>

      {evaluation.key_strengths?.length > 0 && (
        <div className="rs-list">
          <h4>Key strengths</h4>
          <ul>{evaluation.key_strengths.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}

      {evaluation.areas_for_improvement?.length > 0 && (
        <div className="rs-list">
          <h4>Areas for improvement</h4>
          <ul>{evaluation.areas_for_improvement.map((s, i) => <li key={i}>{s}</li>)}</ul>
        </div>
      )}

      {evaluation.github?.profile?.username && (
        <div className="rs-github">
          GitHub: <strong>@{evaluation.github.profile.username}</strong>
          {' · '}{evaluation.github.profile.public_repos ?? 0} repos
          {' · '}{evaluation.github.total_projects ?? 0} projects analysed
        </div>
      )}

      <p className="rs-disclaimer">
        {isStaff
          ? '⚠️ This analysis is AI-generated and can vary between runs. Treat it as one signal among many, not a hiring decision on its own.'
          : '⚠️ Scores are generated by an LLM and can vary between runs. Treat this as one signal among many, not a hiring decision on its own.'}
      </p>
    </div>
  );
}
